'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import SiteNav from '@/components/SiteNav';
import LandingFooter from '@/app/landing-page/components/LandingFooter';
import { ArrowRight, Copy, Check } from 'lucide-react';

function CopyPromptBox({ children }: { children: React.ReactNode }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    const plainText = typeof children === 'string' ? children : '';
    navigator.clipboard.writeText(plainText).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div
      className="relative rounded-xl p-6 my-6"
      style={{ backgroundColor: 'rgba(212,137,26,0.05)', border: '1px solid rgba(212,137,26,0.25)', borderLeft: '4px solid #D4891A' }}
    >
      <div className="font-body text-sm pr-10" style={{ color: '#2d3748', lineHeight: '1.85' }}>
        {children}
      </div>
      <button
        onClick={handleCopy}
        className="absolute top-4 right-4 flex items-center gap-1.5 font-body text-xs font-semibold px-3 py-1.5 rounded-lg transition-all duration-150"
        style={{ backgroundColor: copied ? 'rgba(212,137,26,0.15)' : 'rgba(212,137,26,0.1)', color: '#D4891A' }}
        aria-label="Copy prompt"
      >
        {copied ? <Check size={12} /> : <Copy size={12} />}
        {copied ? 'Copied' : 'Copy'}
      </button>
    </div>
  );
}

export default function AiForPresentationsPage() {
  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Using AI To Write Better Presentations",
    "url": "https://thatclicked.com/learn/ai-for-presentations",
    "author": {
      "@type": "Person",
      "name": "Anna Rippon",
      "url": "https://thatclicked.com/about"
    },
    "publisher": {
      "@type": "Organization",
      "name": "That Clicked",
      "url": "https://thatclicked.com"
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://thatclicked.com/learn/ai-for-presentations"
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F9F7F4' }}>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
      />
      <SiteNav currentPath="/learn/ai-for-presentations" />

      <main className="pt-24 pb-24 lg:pt-36 lg:pb-36">
        <div className="max-w-2xl mx-auto px-6 lg:px-0">

          <div className="flex items-center gap-2 mb-6 mt-8">
            <div className="h-px w-8 flex-shrink-0" style={{ backgroundColor: '#D4891A' }} aria-hidden="true" />
            <span className="font-body text-xs font-semibold uppercase tracking-widest" style={{ color: '#D4891A', letterSpacing: '0.12em' }}>
              By Anna Rippon, ThatClicked
            </span>
          </div>

          <h1
            className="font-display font-semibold mb-8"
            style={{ fontSize: 'clamp(1.75rem, 4vw, 2.5rem)', color: '#1B2A4A', lineHeight: '1.18', letterSpacing: '-0.01em' }}
          >
            Using AI To Write Better Presentations
          </h1>

          <div className="h-px w-full mb-10" style={{ backgroundColor: 'rgba(27,42,74,0.12)' }} aria-hidden="true" />

          <div className="space-y-6 font-body" style={{ color: '#2d3748', lineHeight: '1.85', fontSize: '1.05rem' }}>

            <h2 className="font-display font-semibold pt-2" style={{ fontSize: '0.85rem', color: '#D4891A', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              THE SITUATION
            </h2>

            <p>
              You need to build a presentation.
            </p>

            <p>
              You know what you want to say. You have the information. What you do not have is a clear structure, a confident opening slide, or any particular enthusiasm for the two hours it is going to take to pull it together.
            </p>

            <p>
              AI does not build the presentation for you. But it cuts the time significantly and often produces a better structure than most people arrive at alone.
            </p>

            <p>Here is what it can do.</p>

            <h2 className="font-display font-semibold pt-4" style={{ fontSize: '1.25rem', color: '#1B2A4A' }}>
              What AI can do with presentations
            </h2>

            <h3 className="font-display font-semibold pt-2" style={{ fontSize: '1.1rem', color: '#1B2A4A' }}>
              Structure the argument
            </h3>

            <p>
              The hardest part of most presentations is not the content — it is deciding what order things should go in, what to include, and what to leave out.
            </p>

            <p>
              AI is useful here. Tell it what you are presenting, who the audience is, what you want them to think or do differently at the end, and how much time you have. Ask it to suggest a structure.
            </p>

            <p>
              What comes back will not be exactly right. But it will give you a logical sequence to react to — and reacting to a structure is significantly faster than building one from nothing.
            </p>

            <h3 className="font-display font-semibold pt-2" style={{ fontSize: '1.1rem', color: '#1B2A4A' }}>
              Write the opening
            </h3>

            <p>
              The opening of a presentation is where most people spend the most time and have the least confidence. AI can draft three or four alternative openings in under a minute. You choose the one that feels right for your voice and your audience.
            </p>

            <h3 className="font-display font-semibold pt-2" style={{ fontSize: '1.1rem', color: '#1B2A4A' }}>
              Turn bullet points into spoken language
            </h3>

            <p>
              Most people write presentations in bullet points and then have to translate them into something they can actually say out loud. AI can do this translation for you — give it the bullets and ask it to write them as spoken language for a professional audience.
            </p>

            <p>
              This is particularly useful for sections where you know the content but struggle to find the right words.
            </p>

            <h3 className="font-display font-semibold pt-2" style={{ fontSize: '1.1rem', color: '#1B2A4A' }}>
              Stress-test the argument
            </h3>

            <p>
              Before an important presentation, AI can play devil&rsquo;s advocate. Give it your key recommendation or argument and ask it for the five strongest objections someone in the audience might raise. Then prepare your responses. You will walk in significantly better prepared for the questions that matter.
            </p>

            <h3 className="font-display font-semibold pt-2" style={{ fontSize: '1.1rem', color: '#1B2A4A' }}>
              Write the executive summary
            </h3>

            <p>
              For any presentation that has a one-page summary version — and at board level, there always should be — AI can produce a first draft in under two minutes. You give it the full presentation, it produces the summary. You refine it.
            </p>

            <h2 className="font-display font-semibold pt-4" style={{ fontSize: '1.25rem', color: '#1B2A4A' }}>
              What AI cannot do with presentations
            </h2>

            <p>
              AI does not know your audience the way you do. It cannot replicate the specific tone you use with your leadership team, the reference you always make in client presentations, or the particular thing this audience cares most about.
            </p>

            <p>
              It also cannot make a weak argument strong. If the underlying case is not solid, AI will produce a polished version of a weak case. The judgment call about whether the argument holds up is yours.
            </p>

            <h2 className="font-display font-semibold pt-4" style={{ fontSize: '1.25rem', color: '#1B2A4A' }}>
              A prompt that works
            </h2>

            <CopyPromptBox>
              I need to build a presentation for [describe the audience and the occasion]. The key message I want them to take away is: [describe it]. I have [X minutes]. Please suggest a structure for the presentation — what each section should cover and roughly how long to spend on each.
            </CopyPromptBox>

            <p>
              Once you have a structure you are happy with, go section by section. Ask AI to help you write each one with the details you provide. You will move faster than you would alone and you will end up with something more coherent.
            </p>

            <h2 className="font-display font-semibold pt-4" style={{ fontSize: '1.25rem', color: '#1B2A4A' }}>
              Honest expectations
            </h2>

            <p>
              AI is significantly more useful at the structuring and drafting stage of presentations than most people expect. The gains are real.
            </p>

            <p>
              Where it is less useful: the final polish, the moments of genuine insight, and anything that requires deep knowledge of your specific audience. Those parts are still yours.
            </p>

            <p>
              Think of it as a capable collaborator for the groundwork — not a replacement for the judgment you bring to what matters most.
            </p>

          </div>

          {/* Where to go deeper */}
          <div className="mt-14 rounded-xl p-8 border" style={{ backgroundColor: 'white', borderColor: 'rgba(27,42,74,0.12)' }}>
            <h2 className="font-display font-semibold mb-5" style={{ fontSize: '1.15rem', color: '#1B2A4A' }}>
              Where to go deeper
            </h2>
            <p className="font-body text-sm mb-5" style={{ color: '#2d3748', lineHeight: '1.75' }}>
              Module 1 covers Copilot in PowerPoint specifically — including how to use it to generate outlines and improve existing slides. Module 3 covers communication at work, including how to write and deliver more clearly.
            </p>
            <ul className="space-y-2">
              <li>
                <Link href="/free-lesson-screen" className="font-body text-sm" style={{ color: '#1B2A4A' }}>
                  → Module 1: Copilot at Work <span style={{ color: '#6b7280' }}>(paid)</span>
                </Link>
              </li>
              <li>
                <Link href="/free-lesson-screen" className="font-body text-sm" style={{ color: '#1B2A4A' }}>
                  → Module 3: Communication at Work <span style={{ color: '#6b7280' }}>(paid)</span>
                </Link>
              </li>
              <li>
                <Link href="/learn/ai-for-meeting-preparation" className="font-body text-sm" style={{ color: '#1B2A4A' }}>
                  → Related: How AI Can Help You Prepare For Meetings
                </Link>
              </li>
            </ul>
          </div>

          <div className="h-px w-full mt-14 mb-12" style={{ backgroundColor: 'rgba(27,42,74,0.12)' }} aria-hidden="true" />

          <div className="text-center">
            <p className="font-body text-base mb-6" style={{ color: 'rgba(27,42,74,0.7)' }}>That Clicked is a plain-English AI training platform for professionals over 50.</p>
            <Link
              href="/free-lesson-screen"
              className="group inline-flex items-center justify-center gap-2.5 font-body font-semibold text-base px-8 py-4 rounded-xl transition-all duration-200 active:scale-95"
              style={{ backgroundColor: '#D4891A', color: 'white' }}
            >
              Learn more AI skills — first two lessons free
              <ArrowRight size={18} className="transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
          </div>

        </div>
      </main>

      <LandingFooter />
    </div>
  );
}
